// AdminSettings — Routing Restructure (2026-05-16).
//
// Renamed from AdminMore. URL: `/settings`. This is the catch-all
// landing page for admin tools, platform configuration, and account
// surfaces that don't earn a top-nav slot.
//
// Layout: a SECTIONED card grid (not tabs).
//   - Section titles render ONLY when at least one card inside is
//     visible to the current viewer (role + permission). This keeps
//     agent-side viewers from seeing empty admin-only buckets.
//   - Cards keep the existing solid-coloured pill style from AdminMore
//     so the visual vocabulary doesn't change — only the grouping.
//
// Card destinations (post-restructure):
//   Account            — /account/profile, /account/app-icons
//   Platform config    — /settings/games, /settings/rebate (rebate + fees + commission)
//   Operations tools   — /settings/replay, /settings/reassign,
//                        /settings/bots
//   Monitoring         — /operations (kept as a hub with 6 leftover
//                                     monitoring tabs)
//   (Agent-only)       — /me  (My Pool)
//
// The "Sign out" button retains the existing danger-toned full-width
// CTA at the bottom.

import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import Icon from '@baaligames/shared-ui/components/Icon';

type Tone = 'reward' | 'money' | 'agent' | 'tools' | 'identity' | 'data' | 'danger';

type Row = {
  to: string;
  label: string;
  icon: string;
  subtitle?: string;
  permissionKey?: string;
  onlyRole?: 'admin' | 'agent';
  tone: Tone;
};

// 2026-05-16 (OperationsHub merge — Path C): the `monitor` section
// was removed. Its only card was "Operations", which has been merged
// into the Dashboard (InsightRibbon + DrillPanel). Every former
// /operations/* URL now redirects into the Dashboard with `?drill=…`.
// The remaining sections — tools, config, account — keep their
// frequency-ranked order.
type SectionKey = 'tools' | 'config' | 'account';

// Order reflects daily usage frequency for an admin:
//   1. tools   — reactive actions when something needs fixing (replay
//                a bad settlement, move players between agents, debug
//                a bot)
//   2. config  — proactive platform tuning (game balance, commission
//                policy) — occasional, not daily
//   3. account — own profile / app icons — set once, then never touched
//
// Inside each section, cards are also ordered by frequency so the eye
// lands on the most-used surface first.
const SECTION_ORDER: SectionKey[] = ['tools', 'config', 'account'];
const SECTION_META: Record<SectionKey, { title: string; icon: string; toneText: string }> = {
  tools: {
    title: 'Operator tools',
    icon: 'build',
    toneText: 'text-cyan-700 dark:text-cyan-300',
  },
  config: {
    title: 'Game money',
    icon: 'payments',
    toneText: 'text-emerald-700 dark:text-emerald-300',
  },
  account: {
    title: 'Account',
    icon: 'manage_accounts',
    toneText: 'text-indigo-700 dark:text-indigo-300',
  },
};

// ROWS_BY_SECTION encodes the sectioned layout. Each entry is filtered
// against the viewer's role / permissions before render — section
// titles only show when their section has at least one visible card.
//
// Card order inside each section is also frequency-ranked:
//   * tools  — Replay (post-incident settlement fix) before Reassign
//              (org reshuffle) before Bot profiles (rarer debug surface)
//   * config (Game money) — See (report) → Predict (simulator) → Set
//              (master preset, then per-game) → Economics (rebate, limits)
//              → Availability (show/hide/lock)
//   * account — My Pool listed FIRST for agents (their daily entry
//              point); My Profile + App Icons come after. Admins never
//              see My Pool (onlyRole: 'agent' filter), so their first
//              account card is My Profile, then App Icons.
const ROWS_BY_SECTION: Record<SectionKey, Row[]> = {
  // UX Phase 4 (2026-08-08): "Bulk reassign" + "Bot profiles" left this bucket —
  // Bots are now the third People segment (/people/bots) and Bulk reassign is
  // surfaced right in the People area. Their routes still work.
  tools: [
    { to: '/settings/replay',       icon: 'restart_alt',     label: 'Replay games',  subtitle: 'Re-run settlement for archived sessions',                                    tone: 'tools', onlyRole: 'admin' },
    // 2026-05-17 (Phase 7.8 follow-up): full admin_wallet_ledger audit page.
    { to: '/settings/admin-ledger', icon: 'account_balance', label: 'Wallet ledger', subtitle: 'Browse, filter, and verify every admin_wallet_ledger row · SHA-256 chain', tone: 'money', onlyRole: 'admin' },
  ],
  // UX Phase 3.1 (2026-08-08): "Game money" is the home for everything that
  // decides how much each game earns — SEE it, PREDICT it, SET it, then the
  // UX Phase 4 (2026-08-08): "Game money" graduated from a Settings card to its
  // own TOP-LEVEL nav tab (route /game-money). This section is now empty for
  // admins, so its "Game money" header no longer renders on the Settings landing
  // — the hub lives one tap away on the bottom nav instead.
  config: [],
  account: [
    // Agents see My Pool here first — it's their daily dashboard.
    // Admins skip past it (onlyRole gate) so their list starts with
    // My profile.
    { to: '/me',                icon: 'account_circle', label: 'My Pool',     subtitle: 'Dashboard · Wallet · Earnings · Deposit · Leaderboard · Activity', tone: 'data', onlyRole: 'agent', permissionKey: 'earnings' },
    { to: '/account/profile',   icon: 'person',         label: 'My profile',  subtitle: 'Photo · password · email',         tone: 'identity' },
    { to: '/account/app-icons', icon: 'apps',           label: 'App icons',   subtitle: 'PWA icon per role',                tone: 'identity', onlyRole: 'admin' },
  ],
};

const TONES: Record<Tone, { grad: string; ring: string; shadow: string }> = {
  reward: {
    grad: 'from-violet-500 to-fuchsia-600',
    ring: 'ring-violet-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(139,92,246,0.55)]',
  },
  money: {
    grad: 'from-emerald-500 to-teal-600',
    ring: 'ring-emerald-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(16,185,129,0.55)]',
  },
  agent: {
    grad: 'from-amber-500 to-orange-600',
    ring: 'ring-amber-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(245,158,11,0.55)]',
  },
  tools: {
    grad: 'from-sky-500 to-cyan-600',
    ring: 'ring-sky-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(14,165,233,0.55)]',
  },
  identity: {
    grad: 'from-indigo-500 to-violet-600',
    ring: 'ring-indigo-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(99,102,241,0.55)]',
  },
  data: {
    grad: 'from-pink-500 to-rose-600',
    ring: 'ring-pink-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(244,63,94,0.55)]',
  },
  danger: {
    grad: 'from-rose-500 to-red-600',
    ring: 'ring-rose-400/40',
    shadow: 'shadow-[0_10px_30px_-12px_rgba(244,63,94,0.55)]',
  },
};

export default function AdminSettings() {
  const { admin, canDo, logout } = useAdminAuth();
  const nav = useNavigate();
  if (!admin) return null;

  // Filter each section's rows by role + permission. Capture which
  // sections have any visible card so we can hide titles for empty
  // sections (the user's explicit ask: never render an empty bucket).
  const visibleBySection: Record<SectionKey, Row[]> = {
    tools: [], config: [], account: [],
  };
  for (const key of SECTION_ORDER) {
    for (const r of ROWS_BY_SECTION[key]) {
      if (r.onlyRole && r.onlyRole !== admin.role) continue;
      if (r.permissionKey && !canDo(r.permissionKey, 'view')) continue;
      visibleBySection[key].push(r);
    }
  }

  // Global running index for the stagger animation — keeps card-by-
  // card fade-in flowing across section boundaries instead of resetting
  // at each section.
  let cardIndex = 0;

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-6">
      <header className="px-2 lg:px-4 pt-4 lg:pt-2">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-semibold text-neutral-900 dark:text-white tracking-tight">
          Settings
        </h1>
        <p className="text-[12.5px] text-neutral-500 dark:text-white/55 mt-1">
          Monitor the platform, run tools, configure policy, manage your account
        </p>
      </header>

      {SECTION_ORDER.map((key) => {
        const rows = visibleBySection[key];
        if (rows.length === 0) return null;   // section-title gating
        const meta = SECTION_META[key];
        return (
          <section key={key} className="space-y-3">
            <h2 className={`px-2 lg:px-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${meta.toneText}`}>
              <Icon name={meta.icon} size={13} filled />
              {meta.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-2 lg:px-4">
              {rows.map((r) => {
                const t = TONES[r.tone];
                const i = cardIndex++;
                return (
                  <motion.button
                    key={r.to}
                    onClick={() => nav(r.to)}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -2 }}
                    transition={{ delay: 0.03 * i, type: 'spring', stiffness: 380, damping: 26 }}
                    className={`
                      relative group rounded-2xl p-4 lg:p-5 text-left overflow-hidden
                      bg-gradient-to-br ${t.grad}
                      ring-1 ${t.ring} ${t.shadow}
                      text-white
                      hover:scale-[1.01] active:scale-[0.99] transition
                    `}>
                    <div aria-hidden className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/15 blur-2xl" />
                    <div className="relative flex items-start gap-3">
                      <span className="grid place-items-center w-11 h-11 rounded-xl bg-white/20 ring-1 ring-white/25 shrink-0">
                        <Icon name={r.icon} size={22} filled className="text-white" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] font-bold leading-tight">{r.label}</div>
                        {r.subtitle && (
                          <div className="text-[11.5px] mt-0.5 opacity-90 leading-tight">{r.subtitle}</div>
                        )}
                      </div>
                      <Icon name="chevron_right" size={20} className="text-white/85 shrink-0" />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Sign out — danger-toned, kept distinct */}
      <div className="px-2 lg:px-4 pt-2">
        <button
          onClick={async () => {
            await logout();
            nav(admin.role === 'agent' ? '/agent/login' : '/login', { replace: true });
          }}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl
                     bg-rose-50 ring-1 ring-inset ring-rose-200/60 text-rose-700
                     hover:bg-rose-100
                     dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-300
                     dark:hover:bg-rose-500/20
                     py-3 font-semibold transition">
          <Icon name="logout" size={18} /> Sign out
        </button>
      </div>

      <p className="text-center text-[11px] text-neutral-400 dark:text-white/40 pt-2">
        BaaliGames · v0.1
      </p>
    </div>
  );
}
